import type { ClipMetadata } from '@goldcast/player-config/src/types/clip-types';
import type { ClipVersionListWithAuthor } from './types';
import { ClipVersion } from '@/Pages/Clip/ClipPlayer/ClipCustomizer/ClipCustomizerVersioningPanel/types';

/** Versions are fetched in one page; the UI does not paginate. */
export const VERSION_PAGE_SIZE = 100;

/** The API row carries everything but the display numbering, which toDisplayVersions fills in. */
function mapToClipVersion(v: ClipVersionListWithAuthor): ClipVersion {
  return {
    ...v,
    displayIndex: v.version_index,
    restoredFromDisplayIndex: v.restored_from
  };
}

/**
 * The index the original version holds, which the display numbering is measured from.
 *
 * The backend numbers the first version 1, so the original cannot be recognised by index 0 —
 * it is identified by position instead, as the lowest index held. Only trustworthy when the
 * whole history fits in one page; beyond that the oldest row fetched is not the original, so
 * this falls back to 0 and no version is labelled as the original.
 */
function getOriginalVersionIndex(versions: ClipVersion[]): number {
  if (versions.length === 0 || versions.length >= VERSION_PAGE_SIZE) {
    return 0;
  }
  return Math.min(...versions.map(v => v.version_index));
}

/**
 * Turns an API page into the newest-first, display-numbered list the UI renders.
 *
 * Sorted here rather than trusting the endpoint's order, because `versions[0]` is treated as
 * the latest version throughout the UI. Display indexes are resolved once, here, so no
 * component has to subtract the baseline for itself.
 */
export function toDisplayVersions(results: ClipVersionListWithAuthor[]): ClipVersion[] {
  const sorted = results.map(mapToClipVersion).sort((a, b) => b.version_index - a.version_index);
  const baseline = getOriginalVersionIndex(sorted);

  return sorted.map(version => ({
    ...version,
    displayIndex: version.version_index - baseline,
    restoredFromDisplayIndex: typeof version.restored_from === 'number' ? version.restored_from - baseline : null
  }));
}

/** Empties every captions array in place, mirroring what the backend stores. */
function emptyCaptionsDeep(node: unknown): void {
  if (Array.isArray(node)) {
    node.forEach(emptyCaptionsDeep);
    return;
  }
  if (node && typeof node === 'object') {
    const record = node as Record<string, unknown>;
    Object.keys(record).forEach(key => {
      if (key === 'captions' && Array.isArray(record[key])) {
        record[key] = [];
      } else {
        emptyCaptionsDeep(record[key]);
      }
    });
  }
}

/** A clone of the metadata with every captions array emptied, as captions-recomputable versions are stored. */
export function stripCaptions(assetMetadata: ClipMetadata): ClipMetadata {
  const cloned = structuredClone(assetMetadata);
  emptyCaptionsDeep(cloned);
  return cloned;
}
